import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import accountActivityService from '../../../services/accountActivityService';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const PasswordManagement = () => {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showStrength, setShowStrength] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const passwordRequirements = [
    { id: 1, text: 'At least 12 characters long', met: newPassword?.length >= 12 },
    { id: 2, text: 'Contains uppercase letter', met: /[A-Z]/?.test(newPassword) },
    { id: 3, text: 'Contains lowercase letter', met: /[a-z]/?.test(newPassword) },
    { id: 4, text: 'Contains number', met: /[0-9]/?.test(newPassword) },
    { id: 5, text: 'Contains special character', met: /[!@#$%^&*]/?.test(newPassword) },
    { id: 6, text: 'Not a common password', met: newPassword?.length > 0 && !['password', '123456', 'qwerty']?.includes(newPassword?.toLowerCase()) }
  ];

  const calculateStrength = () => {
    const metCount = passwordRequirements?.filter(req => req?.met)?.length;
    if (metCount <= 2) return { label: 'Weak', color: 'bg-error', width: '33%' };
    if (metCount <= 4) return { label: 'Medium', color: 'bg-warning', width: '66%' };
    return { label: 'Strong', color: 'bg-success', width: '100%' };
  };

  const strength = calculateStrength();

  const passwordHistory = [
    { id: 1, date: '2026-01-10 18:45:12', device: 'MacBook Pro 16"', location: 'San Francisco, CA' },
    { id: 2, date: '2025-12-15 10:30:45', device: 'iPhone 15 Pro', location: 'San Francisco, CA' },
    { id: 3, date: '2025-11-20 14:20:30', device: 'MacBook Pro 16"', location: 'Oakland, CA' }
  ];

  const handlePasswordChange = () => {
    if (currentPassword === 'Kryptostash2026!' && newPassword === confirmPassword && passwordRequirements?.every(req => req?.met)) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowStrength(false);
    }
  };

  const handleChangePassword = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword?.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase?.auth?.updateUser({
        password: newPassword
      });

      if (error) throw error;

      // Log password change activity
      if (user) {
        await accountActivityService?.logActivity(user?.id, {
          activityType: 'password_change',
          description: 'Password changed successfully',
          ipAddress: 'N/A'
        });
      }

      setSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setError(error?.message || 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-md p-4 md:p-6 lg:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="Lock" size={24} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">Password Management</h2>
          <p className="text-sm text-muted-foreground">Update your password and review security requirements</p>
        </div>
      </div>
      <div className="bg-accent/10 border border-accent/30 rounded-lg p-4 mb-6 flex items-start gap-3">
        <Icon name="Info" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-foreground mb-2">
            <strong>Mock Credentials for Testing:</strong>
          </p>
          <p className="text-sm text-muted-foreground">
            Current Password: <span className="font-mono bg-muted px-2 py-0.5 rounded">Kryptostash2026!</span>
          </p>
        </div>
      </div>
      <div className="space-y-4 mb-6">
        <Input
          label="Current Password"
          type="password"
          placeholder="Enter current password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e?.target?.value)}
          required
        />
        <Input
          label="New Password"
          type="password"
          placeholder="Enter new password"
          value={newPassword}
          onChange={(e) => {
            setNewPassword(e?.target?.value);
            setShowStrength(e?.target?.value?.length > 0);
          }}
          required
        />
        {showStrength && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Password Strength:</span>
              <span className={`font-medium ${strength?.color?.replace('bg-', 'text-')}`}>{strength?.label}</span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div className={`h-full ${strength?.color} transition-all duration-300`} style={{ width: strength?.width }}></div>
            </div>
          </div>
        )}
        <Input
          label="Confirm New Password"
          type="password"
          placeholder="Confirm new password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e?.target?.value)}
          error={confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
          required
        />
      </div>
      <div className="bg-muted/50 rounded-lg p-4 mb-6">
        <h3 className="text-base font-medium text-foreground mb-3">Password Requirements</h3>
        <div className="space-y-2">
          {passwordRequirements?.map((req) => (
            <div key={req?.id} className="flex items-center gap-2">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${req?.met ? 'bg-success' : 'bg-muted-foreground/20'}`}>
                {req?.met && <Icon name="Check" size={14} color="#FFFFFF" />}
              </div>
              <span className={`text-sm ${req?.met ? 'text-success' : 'text-muted-foreground'}`}>{req?.text}</span>
            </div>
          ))}
        </div>
      </div>
      <Button
        variant="default"
        onClick={handlePasswordChange}
        disabled={!currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword || !passwordRequirements?.every(req => req?.met)}
        fullWidth
      >
        Update Password
      </Button>
      <div className="mt-8 pt-6 border-t border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4">Password Change History</h3>
        <div className="space-y-3">
          {passwordHistory?.map((entry) => (
            <div key={entry?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="Clock" size={16} color="var(--color-muted-foreground)" />
                <div>
                  <p className="text-sm font-medium text-foreground">{entry?.date}</p>
                  <p className="text-xs text-muted-foreground">{entry?.device} • {entry?.location}</p>
                </div>
              </div>
              <Icon name="CheckCircle" size={16} color="var(--color-success)" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PasswordManagement;